import { useState, useEffect } from "react";
import { api } from "../api";
import {
  formatMonth,
  comma,
  pct,
  calcAccuracy,
  accuracyColor,
  accuracyLabel,
  diffInfo,
} from "../constants";
import Card from "./ui/Card";
import LoadingSpinner from "./ui/LoadingSpinner";
import MonthPicker from "./ui/MonthPicker";

const SEND_TIME_COLORS = {
  before: { bg: "#22c55e", label: "قبل" },
  after: { bg: "#f59e0b", label: "بعد" },
  not_sent: { bg: "#ef4444", label: "لم يُرسل" },
};
const REGION_COLORS = [
  "#38bdf8", "#a78bfa", "#f59e0b", "#ef4444", "#22c55e",
  "#f97316", "#06b6d4", "#8b5cf6", "#14b8a6", "#e11d48",
];

export default function Dashboard({ month, setMonth, toast }) {
  const [data, setData] = useState(null);
  const [view, setView] = useState("full");

  useEffect(() => {
    api(`/dashboard?month=${month}`)
      .then(setData)
      .catch(() => toast?.("خطأ في تحميل لوحة التحكم", "error"));
  }, [month]);

  if (!data) return <LoadingSpinner />;

  const {
    totals = {},
    by_reviewer = [],
    by_reviewer_prev = [],
    diff_reasons = [],
    alerts = [],
    open_deficits = 0,
    regions = [],
    daily_trend = [],
    send_time = [],
    prev_totals = null,
    total_stations = 0,
    covered_stations = 0,
  } = data;

  const totalOp = totals.total_op || 0;
  const totalCam = totals.total_cam || 0;
  // "الفروقات" = الفرق الصافي فقط (تشغيلي − كاميرات)، مش مجموع القيمة
  // المطلقة لكل سجل — رقم واحد بسيط يتطابق مع طرح الرقمين الظاهرين فوق.
  const diffI = diffInfo(totalOp - totalCam);
  const totalDiff = diffI.magnitude;
  const totalEntries = totals.total_entries || 0;
  const activeReviewers = totals.active_reviewers || 0;
  // Reconciled-days-only, same reasoning as the regional/reviewer accuracy
  // below: raw totalCam/totalOp is skewed mid-month by the supervisor's
  // entry lag, not by actual accuracy.
  const overallAcc = calcAccuracy(totals.rec_cam || 0, totals.rec_op || 0);

  const prevOp = prev_totals?.op || 0;
  const prevCam = prev_totals?.cam || 0;
  const trendOp = prevOp > 0 ? ((totalOp - prevOp) / prevOp * 100).toFixed(1) : null;
  const trendCam = prevCam > 0 ? ((totalCam - prevCam) / prevCam * 100).toFixed(1) : null;

  const sortedReviewers = [...by_reviewer].sort((a, b) => {
    return (calcAccuracy(b.rec_cam, b.rec_op) || 0) - (calcAccuracy(a.rec_cam, a.rec_op) || 0);
  });

  const maxDailyOp = Math.max(...daily_trend.map(d => d.op || 0), 1);

  const sendTimeTotal = send_time.reduce((s, t) => s + (t.cnt || 0), 0) || 1;

  return (
    <div>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20, flexWrap: "wrap", gap: 10 }}>
        <div>
          <h2 style={{ fontSize: 22, fontWeight: 800, margin: 0, color: "#f1f5f9" }}>
            📊 لوحة التحكم
          </h2>
          <p style={{ color: "#64748b", fontSize: 13, margin: "4px 0 0" }}>
            {formatMonth(month)} — نظرة شاملة على أداء المراجعة
          </p>
        </div>
        <MonthPicker value={month} onChange={setMonth} />
      </div>

      {/* KPI Cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: 10, marginBottom: 18 }}>
        <KpiCard icon="⚡" label="الردود التشغيلية" value={comma(totalOp)} sub={trendOp !== null ? `${trendOp > 0 ? "▲" : "▼"} ${Math.abs(trendOp)}% عن الشهر السابق` : null} subColor={trendOp > 0 ? "#22c55e" : "#ef4444"} color="#38bdf8" title="إجمالي عدد الردود التشغيلية المسجلة خلال الشهر" />
        <KpiCard icon="🎥" label="ردود الكاميرات" value={comma(totalCam)} sub={trendCam !== null ? `${trendCam > 0 ? "▲" : "▼"} ${Math.abs(trendCam)}% عن الشهر السابق` : null} subColor={trendCam > 0 ? "#22c55e" : "#ef4444"} color="#a78bfa" title="إجمالي عدد ردود الكاميرات المسجلة خلال الشهر" />
        <KpiCard icon="🎯" label="دقة المراجعة" value={overallAcc !== null ? pct(overallAcc) : "—"} sub="100% − |الفرق| ÷ تشغيلي (الزيادة والنقص = خطأ)" color={overallAcc !== null ? accuracyColor(overallAcc) : "#64748b"} title="مقياس مختلف عن «الفروقات» المجاورة: الدقة تُحسب يوميًا وتحتسب كل زيادة أو نقص كخطأ منفصل، حتى لو تعادلا وألغيا بعضهما في «الفروقات» (الفرق الصافي)." />
        <KpiCard icon="↔️" label="الفروقات" value={comma(totalDiff)} sub={diffI.label} color={diffI.color} title="الفرق الصافي: إجمالي الردود التشغيلية − إجمالي ردود الكاميرات" />
        <KpiCard icon="👥" label="المراجعون النشطون" value={activeReviewers} sub={`${comma(totalEntries)} دخول`} color="#06b6d4" title="عدد المراجعين الذين سجلوا دخولًا خلال الشهر" />
        <KpiCard icon="⚠️" label="عجز مفتوح" value={open_deficits} color={open_deficits > 0 ? "#ef4444" : "#22c55e"} title="عدد حالات العجز التي لم يتم إغلاقها بعد" />
        <KpiCard icon="🏭" label="تغطية المحطات" value={`${covered_stations}/${total_stations}`} sub={total_stations > 0 ? `${(covered_stations / total_stations * 100).toFixed(0)}%` : ""} color="#14b8a6" title="عدد المحطات النشطة التي كانت معيَّنة لمراجع خلال الشهر المحدد، من إجمالي المحطات النشطة حاليًا (بغض النظر عن إدخال بيانات هذا الشهر)" />
      </div>

      {/* Two-column layout */}
      <div style={{ display: "grid", gridTemplateColumns: "1.4fr 1fr", gap: 14, marginBottom: 14 }}>
        {/* Left: Regional + Accuracy */}
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {/* Regional Performance */}
          {regions.length > 0 && (
            <Card>
              <h3 title="نسبة دقة كل منطقة بناءً على إجمالي الردود التشغيلية والكاميرات" style={{ color: "#f1f5f9", margin: "0 0 14px", fontSize: 15, fontWeight: 700 }}>
                🗺️ الأداء حسب المناطق
              </h3>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {regions.map((r, i) => {
                  // Accuracy is judged only on reconciled days (both camera
                  // and operational replies entered) — raw totals would be
                  // misleading mid-month since the supervisor's entries lag.
                  const acc = calcAccuracy(r.rec_cam, r.rec_op);
                  const c = REGION_COLORS[i % REGION_COLORS.length];
                  return (
                    <div key={r.region}>
                      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 3, fontSize: 12 }}>
                        <span style={{ color: "#cbd5e1", fontWeight: 600 }}>{r.region}</span>
                        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                          <span style={{ color: "#64748b" }}>{comma(r.total_op)} / {comma(r.total_cam)}</span>
                          <span style={{ color: accuracyColor(acc), fontWeight: 700 }}>{acc !== null ? pct(acc) : "—"}</span>
                          <span style={{ color: "#64748b" }}>({comma(r.entry_count)})</span>
                        </div>
                      </div>
                      <div style={{ background: "#1e293b", borderRadius: 99, height: 4 }}>
                        <div style={{ width: acc !== null ? `${(acc * 100).toFixed(1)}%` : "0%", background: `linear-gradient(90deg,${c}66,${c})`, height: "100%", borderRadius: 99 }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>
          )}

          {/* Reviewer Accuracy */}
          <Card>
            <h3 title="ترتيب المراجعين حسب دقة المراجعة ونسبة التطابق بين الردود" style={{ color: "#f1f5f9", margin: "0 0 14px", fontSize: 15, fontWeight: 700 }}>
              📈 دقة المراجعين
            </h3>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {sortedReviewers.map((r, idx) => {
                // Same reconciled-days-only logic as the regional accuracy
                // above — raw r.cam/r.op would show a false low accuracy for
                // reviewers whose supervisor hasn't caught up entering
                // operational_replies yet this month.
                const acc = calcAccuracy(r.rec_cam, r.rec_op);
                const c = accuracyColor(acc);
                return (
                  <div key={r.id}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 2, fontSize: 12 }}>
                      <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                        <span style={{ color: "#64748b", fontSize: 10, fontWeight: 700, minWidth: 16 }}>#{idx + 1}</span>
                        <span style={{ color: "#cbd5e1" }}>{r.name.split(" ").slice(0, 2).join(" ")}</span>
                      </div>
                      <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                        <span style={{ color: "#64748b", fontSize: 10 }}>{comma(r.cam)}/{comma(r.op)}</span>
                        <span style={{ color: c, fontWeight: 700, fontSize: 12 }}>{acc !== null ? pct(acc) : "—"}</span>
                        <span style={{
                          background: `${c}22`, color: c, borderRadius: 4, padding: "0 5px",
                          fontSize: 9, border: `1px solid ${c}44`, fontWeight: 600,
                        }}>{accuracyLabel(acc)}</span>
                      </div>
                    </div>
                    <div style={{ background: "#1e293b", borderRadius: 99, height: 5 }}>
                      <div style={{
                        width: acc !== null ? pct(acc) : "0%",
                        background: `linear-gradient(90deg,${c}66,${c})`,
                        height: "100%", borderRadius: 99,
                        transition: "width 0.5s ease",
                      }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        </div>

        {/* Right: Send Time + Daily Trend + Diff Reasons */}
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {/* Send Time Distribution */}
          {send_time.length > 0 && (
            <Card>
              <h3 title="توزيع الإدخالات حسب وقت الإرسال: قبل الموعد، بعد الموعد، أو لم يُرسل" style={{ color: "#f1f5f9", margin: "0 0 12px", fontSize: 15, fontWeight: 700 }}>
                ⏱️ حالة الإرسال
              </h3>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {["before", "after", "not_sent"].map(st => {
                  const item = send_time.find(s => s.send_time_status === st);
                  const cnt = item?.cnt || 0;
                  const stc = SEND_TIME_COLORS[st];
                  return (
                    <div key={st}>
                      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 3, fontSize: 12 }}>
                        <span style={{ color: stc.bg, fontWeight: 600 }}>{stc.label}</span>
                        <div style={{ display: "flex", gap: 8 }}>
                          <span style={{ color: "#f1f5f9", fontWeight: 700 }}>{comma(cnt)}</span>
                          <span style={{ color: "#64748b" }}>({(cnt / sendTimeTotal * 100).toFixed(1)}%)</span>
                        </div>
                      </div>
                      <div style={{ background: "#1e293b", borderRadius: 99, height: 6 }}>
                        <div style={{
                          width: `${(cnt / sendTimeTotal * 100).toFixed(1)}%`,
                          background: stc.bg,
                          height: "100%", borderRadius: 99,
                        }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>
          )}

          {/* Daily Trend (mini sparkline bars) */}
          {daily_trend.length > 0 && (
            <Card>
              <h3 style={{ color: "#f1f5f9", margin: "0 0 10px", fontSize: 15, fontWeight: 700 }}>
                📅 الاتجاه اليومي
              </h3>
              <div style={{ display: "flex", alignItems: "flex-end", gap: 2, height: 70 }}>
                {daily_trend.slice(-31).map((d, i) => {
                  const h = Math.max(3, ((d.op || 0) / maxDailyOp) * 60);
                  const isHigh = (d.diff || 0) > 50;
                  return (
                    <div key={d.entry_date} style={{
                      flex: 1, display: "flex", flexDirection: "column",
                      alignItems: "center", justifyContent: "flex-end",
                      height: "100%", position: "relative",
                      cursor: "pointer", title: `${d.entry_date}: ${comma(d.op)} تشغيلي, فرق ${comma(d.diff)}`,
                    }}>
                      <div style={{
                        width: "100%", maxWidth: 10, height: h,
                        background: isHigh
                          ? "linear-gradient(180deg,#ef4444,#dc2626)"
                          : "linear-gradient(180deg,#38bdf8,#0284c7)",
                        borderRadius: "2px 2px 0 0",
                        opacity: 0.8,
                        transition: "opacity 0.2s",
                      }} />
                    </div>
                  );
                })}
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", marginTop: 6 }}>
                <span style={{ color: "#64748b", fontSize: 9 }}>{daily_trend[0]?.entry_date?.slice(5) || ""}</span>
                <span style={{ color: "#64748b", fontSize: 9 }}>{daily_trend[daily_trend.length - 1]?.entry_date?.slice(5) || ""}</span>
              </div>
              <div style={{ display: "flex", gap: 14, marginTop: 6, fontSize: 10, color: "#64748b" }}>
                <span>■ <span style={{ color: "#38bdf8" }}>عادي</span></span>
                <span>■ <span style={{ color: "#ef4444" }}>فرق كبير</span></span>
              </div>
            </Card>
          )}

          {/* Diff Reasons */}
          {diff_reasons.length > 0 && (
            <Card>
              <h3 title="الأسباب الأكثر شيوعًا للاختلاف بين الردود التشغيلية والكاميرات" style={{ color: "#f1f5f9", margin: "0 0 10px", fontSize: 15, fontWeight: 700 }}>
                🔍 أسباب الفروقات
              </h3>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {diff_reasons.map((d, i) => {
                  const colors = ["#ef4444", "#f59e0b", "#8b5cf6", "#06b6d4", "#22c55e", "#f97316", "#e11d48", "#14b8a6", "#38bdf8", "#a78bfa"];
                  const c = colors[i % colors.length];
                  const maxCnt = diff_reasons[0]?.cnt || 1;
                  return (
                    <div key={i}>
                      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 2 }}>
                        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                          <div style={{ width: 7, height: 7, borderRadius: "50%", background: c }} />
                          <span style={{ color: "#cbd5e1" }}>{d.diff_reason}</span>
                        </div>
                        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                          <span style={{ color: "#f1f5f9", fontWeight: 700 }}>{d.cnt}</span>
                          <span style={{ color: "#64748b" }}>({(d.cnt / maxCnt * 100).toFixed(0)}%)</span>
                        </div>
                      </div>
                      <div style={{ background: "#1e293b", borderRadius: 99, height: 4 }}>
                        <div style={{ width: `${(d.cnt / maxCnt * 100).toFixed(0)}%`, background: c, height: "100%", borderRadius: 99 }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>
          )}

          {/* Alerts */}
          {alerts.length > 0 && (
            <Card>
              <h3 title="مراجعون بدقة أقل من 95% أو فرق إجمالي أكبر من 200" style={{ color: "#f1f5f9", margin: "0 0 10px", fontSize: 15, fontWeight: 700 }}>
                🚨 تنبيهات
              </h3>
              {alerts.map((a, i) => (
                <div key={i} style={{
                  background: "#0f172a", borderRadius: 8, padding: "8px 12px",
                  border: "1px solid #ef444433", marginBottom: 5,
                  display: "flex", justifyContent: "space-between", alignItems: "center",
                }}>
                  <span style={{ color: "#f1f5f9", fontSize: 13 }}>{a.name}</span>
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    {a.accuracy < 95 && (
                      <span style={{ color: "#ef4444", fontSize: 11, background: "#ef444422", padding: "2px 6px", borderRadius: 4 }}>
                        دقة {a.accuracy}%
                      </span>
                    )}
                    {Math.abs(a.total_diff) > 200 && (
                      <span style={{ color: "#f59e0b", fontSize: 11, background: "#f59e0b22", padding: "2px 6px", borderRadius: 4 }}>
                        فرق {comma(diffInfo(a.total_diff).magnitude)} ({diffInfo(a.total_diff).label})
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}

function KpiCard({ icon, label, value, sub, subColor, color = "#38bdf8", title }) {
  return (
    <div title={title} style={{
      background: "linear-gradient(135deg,#0f172a,#1e293b)",
      border: `1px solid ${color}33`,
      borderRadius: 14, padding: "14px 16px",
      boxShadow: `0 0 20px ${color}15`,
      position: "relative", overflow: "hidden",
    }}>
      <div style={{ position: "absolute", top: -10, right: -10, width: 60, height: 60, borderRadius: "50%", background: `${color}08`, pointerEvents: "none" }} />
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
        <span style={{ fontSize: 16 }}>{icon}</span>
        <span style={{ color: "#94a3b8", fontSize: 11 }}>{label}</span>
      </div>
      <div style={{ color, fontSize: 22, fontWeight: 800, lineHeight: 1.2 }}>{value}</div>
      {sub && (
        <div style={{ color: subColor || "#64748b", fontSize: 10, marginTop: 3 }}>
          {sub}
        </div>
      )}
    </div>
  );
}
